import React, { ReactNode } from "react";
import auth from "./FirebaseAuth";

// used for testing to avoid uploading images
export const TEST_USER_ID = "xxx-test-user-id";

export const TEST_FIREBASE_USER: FirebaseUser = {
    uid: TEST_USER_ID,
};

/**
 * This is the exposed user object in the context
 */
export interface FirebaseUser {
    uid: string;
}

export enum FirebaseStatus {
    Loading,
    Out,
    In,
}

interface FirebaseContextInterface {
    status: FirebaseStatus;
    user?: FirebaseUser; // in replacement of FirebaseAuthTypes.User
}

export const FirebaseManagerContext = React.createContext<FirebaseContextInterface>({
    status: FirebaseStatus.Loading,
    user: undefined,
});

type FirebaseManagerProps = {
    children?: ReactNode | undefined;
    testUser?: FirebaseUser; // Used for tests and storybooks
};

const FirebaseManager: React.FC<FirebaseManagerProps> = ({ children, testUser }) => {
    const [firebaseManagerContext, setFirebaseManagerContext] =
        React.useState<FirebaseContextInterface>(
            testUser
                ? {
                      status: FirebaseStatus.In,
                      user: testUser,
                  }
                : {
                      status: FirebaseStatus.Loading,
                  },
        );

    React.useEffect(() => {
        if (testUser) {
            setFirebaseManagerContext((oldFirebaseState) => ({
                ...oldFirebaseState,
                status: FirebaseStatus.In,
                user: testUser,
            }));
            return;
        }
        return auth().onAuthStateChanged((user) => {
            if (user) {
                setFirebaseManagerContext({
                    status: FirebaseStatus.In,
                    user,
                });
                return;
            }
            // User is signed out
            setFirebaseManagerContext({
                status: FirebaseStatus.Out,
            });
        });
    }, [testUser]);
    return (
        <FirebaseManagerContext.Provider value={firebaseManagerContext}>
            {children}
        </FirebaseManagerContext.Provider>
    );
};

export default FirebaseManager;
